(ns renolit-roofing-service.layout.form.form-item.file
  (:require [clojure.string :as str]
            [goog.object :as g]
            [medley.core :as medley]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [renolit-roofing-service.layout.form.error :as form.error]
            [renolit-roofing-service.layout.form.form-item :as form.form-item]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.shared.util.time :as util.time]
            [renolit-roofing-service.util :as client.util]
            [renolit-roofing-service.util.file :as util.file]))

(defn- validate-file
  [{:keys [size-limit allowed-filetypes]}
   {:keys [size type] :as file}]
  (cond
    (and size-limit (> size size-limit))
    {:file file
     :reason :size-too-big}

    (and allowed-filetypes
         (not (get (set allowed-filetypes) type)))
    {:file file
     :reason :filetype-not-allowed}))

(defn- on-files-added
  [{:keys [state path on-files-added on-file-added on-file-invalid multiple?] :as opts} files]
  (let [errors (keep (partial validate-file opts) files)]
    (if (seq errors)
      (client.util/execute-callback on-file-invalid errors)
      (do
        (when (and state
                   multiple?
                   (not (:prevent-default? on-files-added)))
          (swap! state update-in path #(vec (concat % files))))
        (when (and state
                   (not multiple?)
                   (not (:prevent-default? on-file-added)))
          (swap! state assoc-in path (first files)))
        (if multiple?
          (client.util/execute-callback on-files-added files)
          (client.util/execute-callback on-file-added (first files)))))))

(defn- on-file-removed
  [{:keys [state path on-file-removed multiple?]} file]
  (when (and state (not (:prevent-default? on-file-removed)))
    (if multiple?
      (swap! state update-in path
             (fn [files]
               (->> files
                    (remove
                     #(or (and (:id %) (= (:id file) (:id %)))
                          (and (:fe-id %) (= (:fe-id file) (:fe-id %)))))
                    (vec))))
      (swap! state medley/dissoc-in path)))
  (client.util/execute-callback on-file-removed file))

(defn- file-select-input
  [{:keys [allowed-filetypes disabled?] :as opts}]
  [:input.file-uploader__input
   (cond->
    {:type "file"
     :multiple true
     :disabled (boolean disabled?)
     :on-change
     (fn [e]
       (let [browser-files (g/getValueByKeys e "target" "files")
             files (map util.file/browser-file->file browser-files)]
         (on-files-added opts files)))}

     (seq allowed-filetypes)
     (assoc :accept (str/join "," allowed-filetypes)))])

(defn- no-files
  [{:keys [multiple? title-transl-kw icon?] :as opts
    :or {icon? true}}]
  [:label.file-uploader__upload-box
   (when icon?
     [layout.icon/icon
      {:key :add-square
       :class "file-uploader__upload-icon"}])
   [:div.file-uploader__upload-box-title-container
    [:span.file-uploader__upload-box-title
     (cond
       title-transl-kw
       (t [title-transl-kw])

       multiple?
       (t [:form.file-uploader/drop-files])

       :else
       (t [:form.file-uploader/drop-file]))]]
   [file-select-input opts]])

(defn- file-listing
  [{:keys [state path disabled?] :as opts}]
  [:div.file-uploader__file-listing
   [:div.file-uploader__files
    [:div.file-uploader__file
     [:div.file-uploader__file-name-header
      [:span "Name"]]
     [:div.file-uploader__file-format-header
      [:span "Type"]]
     [:div.file-uploader__file-opts-header
      [:span "Size"]]]
    (for [file (get-in @state path)]
      ^{:key (or (:id file) (:fe-id file))}
      [:div.file-uploader__file
       [:div.file-uploader__file-name-container
        [:span (:name file)]]
       [:div.file-uploader__file-format-container
        [:span (:extension file)]]
       [:div.file-uploader__file-opts-container
        [:span (util.file/humanize-file-size (:size file))]]
       [:div.file-uploader__delete-btn
        {:disabled (boolean disabled?)
         :on-click
         (fn [_]
           (on-file-removed opts file))}
        [layout.icon/icon
         {:key :delete-bin
          :clickable? true
          :class ["file-uploader__delete-btn-icon"]}]]])]
   [:label.file-uploader__upload-more-btn
    [layout.icon/icon
     {:key :add-circle}]
    (t [:form.file-uploader/add-more-files])
    [file-select-input opts]]])

(defn- single-file
  [{:keys [state path disabled?] :as opts}]
  (let [file (get-in @state path)]
    [:div.file-uploader__single-file-display
     [:div.file-uploader__single-file-file
      [:span
       (str (:name file) "." (:extension file))]
      [:span.file-uploader__single-file-info
       (when (and (:size file)
                  (:last-modified-at file))
         (str
          (util.file/humanize-file-size (:size file))
          " · "
          (util.time/instant->zoned-date-time-string
           (:last-modified-at file)
           {:format-pattern (t [:common.date-time-formats/date])})))]]
     (when-not disabled?
       [:div.file-uploader__single-file-options
        [:label.file-uploader__upload-more-btn
         [layout.icon/icon {:key :add-circle}]
         (t [:form.file-uploader/replace-file])
         [file-select-input opts]]
        [:button.flat-btn.file-uploader__delete-btn
         {:type "button"
          :on-click
          (fn [_]
            (on-file-removed opts file))}
         [layout.icon/icon
          {:key :delete-bin}]
         (t [:form.file-uploader/remove-file])]])]))

(defn- file-dragging
  [{:keys [multiple? title-transl-kw] :as _opts}]
  [:label.file-uploader__upload-box.file-uploader__upload-box--dragging
   [layout.icon/icon
    {:key :add-square
     :class "file-uploader__upload-icon"}]
   [:div.file-uploader__upload-box-title-container
    [:span.file-uploader__upload-box-title
     (cond
       title-transl-kw
       (t [title-transl-kw])

       multiple?
       (t [:form.file-uploader/drop-files])

       :else
       (t [:form.file-uploader/drop-file]))]]])

(defmethod form.form-item/form-item-component :file
  [{:keys [context path] :as _opts}]
  (let [component-id (str (random-uuid))
        dragging? (r/atom false)
        form-errors (rf/subscribe [::form.error/errors context path])]
    (fn [{:keys [state path label label-transl-kw disabled?] :as opts}]
      [:div.form-item
       {:class [(when disabled?
                  "form-item--disabled")]}
       (when (or label-transl-kw label)
         [:label.form-item__label
          (or (t [label-transl-kw]) label)])
       [:div.file-uploader
        {:id component-id
         :on-drag-over #(when-not disabled?
                          (reset! dragging? true))
         :on-drag-leave #(reset! dragging? false)
         :on-drag-end #(reset! dragging? false)
         :on-drop
         (fn [e]
           (let [files (util.file/get-files-from-drag-event e)]
             (when-not disabled?
               (on-files-added opts files))
             (reset! dragging? false)))
         :class [(when (seq @form-errors)
                   "file-uploader--error")]}
        (cond
          @dragging?
          [file-dragging opts]

          (and state (map? (get-in @state path)))
          [single-file opts]

          (and state (seq (get-in @state path)))
          [file-listing opts]

          :else
          [no-files opts])]
       (when (seq @form-errors)
         [form.error/form-item-errors @form-errors])])))
